import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

// Import Material UI components
import {
  Box, Typography, Paper, Button, Card, CardContent, CardActions,
  Divider, Alert, CircularProgress, Grid, List, ListItem, ListItemIcon, 
  ListItemText, Chip, Tooltip, Container, Tabs, Tab
} from '@mui/material';

// Import Material UI icons
import {
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  StarOutline as StarOutlineIcon,
  ArrowForward as ArrowForwardIcon,
  CompareArrows as CompareArrowsIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';

// Format currency
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const TenantSubscriptionPlans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load tenant-specific subscription plans
        const plansResponse = await axios.get('/api/v1/member-subscription-plans/available');
        
        // Get current subscription if any
        try {
          const subscriptionResponse = await axios.get('/api/v1/member-subscriptions/me');
          if (subscriptionResponse.data && subscriptionResponse.data.data) {
            setCurrentSubscription(subscriptionResponse.data.data);
          }
        } catch (subErr) {
          // No active subscription - this is normal so we don't show an error
          console.log('No active subscription found');
        }
        
        if (plansResponse.data && Array.isArray(plansResponse.data.data)) {
          const transformedPlans = plansResponse.data.data.map(plan => ({
            ...plan,
            features: plan.features || ['Basic membership'],
            interval: plan.interval || 'month'
          }));
          setPlans(transformedPlans);
        } else {
          setError("No subscription plans available. Please contact your administrator.");
        }
      } catch (err) {
        console.error("Failed to load subscription plans:", err);
        const errorMessage = err.response?.data?.error || 'Error loading subscription plans';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  // Handle subscription action
  const handleSubscribe = (planId) => {
    navigate(`/member/subscribe/${planId}`);
  };
  
  // Check if a plan is the member's current plan
  const isCurrentPlan = (planId) => {
    return currentSubscription && currentSubscription.plan && currentSubscription.plan._id === planId;
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Filter plans based on selected tab
  const filteredPlans = tabValue === 0 
    ? plans 
    : tabValue === 1 
      ? plans.filter(plan => plan.interval === 'month')
      : plans.filter(plan => plan.interval === 'year');
  
  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium' }}>
          Membership Plans
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Choose the perfect membership plan to enhance your experience
        </Typography>
      </Paper>

      {/* Error message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Current plan notification */}
      {currentSubscription && (
        <Alert 
          severity="info" 
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                Your current plan: <strong>{currentSubscription.plan.name}</strong>
              </Typography>
              <Typography variant="body2">
                {currentSubscription.status === 'active'
                  ? `Your subscription is active until ${new Date(currentSubscription.expiresAt).toLocaleDateString()}`
                  : 'Your subscription needs attention.'
                }
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              component={Link}
              to="/member/my-subscription"
              startIcon={<CompareArrowsIcon />}
            >
              Manage Subscription
            </Button>
          </Box>
        </Alert>
      )}

      {/* Plans filter tabs */}
      {plans.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="All Plans" />
            <Tab label="Monthly" />
            <Tab label="Annual" />
          </Tabs>
        </Box>
      )}

      {/* Plans Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {filteredPlans.length > 0 ? (
          filteredPlans.map((plan) => (
            <Grid item xs={12} md={4} key={plan._id}>
              <Card 
                elevation={plan.isRecommended ? 3 : 1} 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  },
                  border: plan.isRecommended ? '2px solid #3f51b5' : 'none',
                }}
              >
                {plan.isRecommended && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: 0,
                      right: 0,
                      textAlign: 'center',
                      zIndex: 1
                    }}
                  >
                    <Chip
                      label="Recommended"
                      color="primary"
                      size="small"
                      icon={<StarIcon />}
                    />
                  </Box>
                )}

                <CardContent sx={{ pt: plan.isRecommended ? 4 : 2 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {plan.name}
                  </Typography>
                  
                  <Typography 
                    variant="h4" 
                    component="div" 
                    color="primary"
                    sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}
                  >
                    {formatCurrency(plan.price)}
                    <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                      /{plan.interval}
                    </Typography>
                  </Typography>

                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 3, minHeight: 60 }}
                  >
                    {plan.description || 'A membership subscription plan'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Features:
                  </Typography>
                  
                  <List dense disablePadding>
                    {(plan.features || []).map((feature, index) => (
                      <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                
                <CardActions sx={{ p: 3, pt: 1 }}>
                  {isCurrentPlan(plan._id) ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      component={Link}
                      to="/member/my-subscription"
                    >
                      Your Current Plan
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => handleSubscribe(plan._id)}
                      endIcon={<ShoppingCartIcon />}
                      sx={{
                        background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                        boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                      }}
                    >
                      {currentSubscription ? 'Change Plan' : 'Subscribe'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No subscription plans available for the selected filter. Please try another option or contact your administrator.
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* FAQ Section */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              How do I change my subscription plan?
            </Typography>
            <Typography variant="body2" paragraph>
              You can upgrade or downgrade your plan at any time. Changes to a higher tier will be applied immediately, while downgrades will take effect at the end of your billing cycle.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              What happens when I upgrade my plan?
            </Typography>
            <Typography variant="body2" paragraph>
              When upgrading to a higher-level plan, you'll be charged the prorated difference for the remainder of your billing cycle, and you'll immediately gain access to all new features.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              What payment methods are accepted?
            </Typography>
            <Typography variant="body2" paragraph>
              We accept all major credit cards including Visa, Mastercard, American Express, and Discover. We also support PayPal for recurring payments.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="medium">
              Can I cancel my subscription?
            </Typography>
            <Typography variant="body2" paragraph>
              Yes, you can cancel your subscription at any time from your account settings. After cancellation, you'll still have access until the end of your current billing period.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Need Help Section */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 3, 
          borderRadius: 2, 
          bgcolor: 'rgba(63, 81, 181, 0.05)', 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Need Help Choosing a Plan?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Our team is ready to help you select the best plan for your needs. Contact us for personalized assistance.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          component={Link}
          to="/member/help"
        >
          Contact Support
        </Button>
      </Paper>
    </Container>
  );
};

export default TenantSubscriptionPlans;
